import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Trash2, Upload, AlertCircle } from 'lucide-react';
import {
  getDaRegistrare, getUsciteDaRegistrare, registraFatturaPassiva, listFatturePassive,
  updateFatturaPassiva, deleteFatturaPassiva, getFilePassivaBlob,
  type DaRegistrareItem, type UscitaDaRegistrare, type FatturaPassiva,
} from '../../api/fatturePassive';
import { fmtKg } from '../../lib/saldo';
import FatturaPassivaDettaglioModal from './FatturaPassivaDettaglioModal';
import ProspettoPassivoModal from './ProspettoPassivoModal';
import { useCategorie } from '../../hooks/useCategorie';
import { categoriaLabel } from '../../lib/categorie';

const inputCls = 'text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white';
const eur = (n: number) => `€ ${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type Tab = 'da-registrare' | 'registra' | 'registrate';

interface Props {
  /** `?passiva=<id>`: apre direttamente quella fattura fra le Registrate.
   *  Valore INIZIALE dello stato, non una sincronizzazione — come il
   *  `?fattura=` del canale attivo. */
  passivaDeepLink?: string | null;
}

export default function FatturePassive({ passivaDeepLink = null }: Props) {
  const { t } = useTranslation();
  const { categorie } = useCategorie();   // R4-A: lookup codice → nome_visualizzato
  const [tab, setTab] = useState<Tab>(passivaDeepLink ? 'registrate' : 'da-registrare');

  // ── Da registrare ──
  const [daReg, setDaReg] = useState<DaRegistrareItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Fetta 3: le uscite a terzi stanno nella stessa scheda, in una sezione sua.
  // Si possono selezionare INSIEME ai conferimenti — il backend le accetta
  // mescolate purché dello stesso fornitore — ma non si filtrano per categoria,
  // che un'uscita non ha.
  const [uscite, setUscite] = useState<UscitaDaRegistrare[]>([]);
  const [selUscite, setSelUscite] = useState<Set<string>>(new Set());
  const loadDaReg = useCallback(() => {
    void (async () => {
      const [conf, usc] = await Promise.all([getDaRegistrare(), getUsciteDaRegistrare()]);
      setDaReg(conf); setUscite(usc);
    })();
  }, []);
  useEffect(() => { if (tab === 'da-registrare') loadDaReg(); }, [tab, loadDaReg]);

  // Selezione vincolata a un solo fornitore (la passiva è di un fornitore).
  // Lo decide la PRIMA riga spuntata, di qualunque delle due sezioni.
  const lockedCustomer = useMemo(() => {
    const conf = daReg.find((d) => selected.has(d.conferimento_id));
    if (conf) return conf.customer_id;
    const u = uscite.find((x) => selUscite.has(x.uscita_id));
    return u?.customer_id ?? null;
  }, [selected, daReg, selUscite, uscite]);

  const totSelezionati = selected.size + selUscite.size;

  function toggle(item: DaRegistrareItem) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(item.conferimento_id)) next.delete(item.conferimento_id);
      else next.add(item.conferimento_id);
      return next;
    });
  }

  function toggleUscita(item: UscitaDaRegistrare) {
    setSelUscite((s) => {
      const next = new Set(s);
      if (next.has(item.uscita_id)) next.delete(item.uscita_id);
      else next.add(item.uscita_id);
      return next;
    });
  }

  const selectedUscite = useMemo(
    () => uscite.filter((u) => selUscite.has(u.uscita_id)),
    [uscite, selUscite],
  );

  const selectedItems = useMemo(
    () => daReg.filter((d) => selected.has(d.conferimento_id)),
    [daReg, selected],
  );

  // ── Prospetto (stateless): modale sul fornitore bloccato dalla selezione,
  // con TUTTI i suoi da-registrare preselezionati (la selezione fine sta lì).
  const [prospettoOpen, setProspettoOpen] = useState(false);
  const fornitoreItems = useMemo(
    () => (lockedCustomer ? daReg.filter((d) => d.customer_id === lockedCustomer) : []),
    [daReg, lockedCustomer],
  );

  // ── Registra (form) ──
  const [numeroForn, setNumeroForn] = useState('');
  const [dataFatt, setDataFatt] = useState('');
  const [importo, setImporto] = useState('');
  const [pagata, setPagata] = useState(false);
  const [dataPag, setDataPag] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  function resetForm() {
    setNumeroForn(''); setDataFatt(''); setImporto(''); setPagata(false);
    setDataPag(''); setFile(null); setFormErr(null);
  }

  async function submitRegistra() {
    setFormErr(null);
    if (totSelezionati === 0) { setFormErr(t('fatturazione.passive.err_no_conf')); return; }
    if (!numeroForn.trim() || !dataFatt || !importo.trim()) { setFormErr(t('fatturazione.passive.err_campi')); return; }
    if (pagata && !dataPag) { setFormErr(t('fatturazione.passive.err_data_pag')); return; }
    if (!file) { setFormErr(t('fatturazione.passive.err_file')); return; }
    setSaving(true);
    try {
      await registraFatturaPassiva({
        numero_fornitore: numeroForn.trim(),
        data: dataFatt,
        importo: Number(importo.replace(',', '.')),
        conferimento_ids: [...selected],
        uscita_ids: [...selUscite],
        pagata,
        data_pagamento: pagata ? dataPag : null,
        file,
      });
      resetForm();
      setSelected(new Set());
      setSelUscite(new Set());
      setTab('registrate');
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setFormErr(typeof detail === 'string' ? detail : t('fatturazione.passive.err_registrazione'));
    } finally { setSaving(false); }
  }

  // ── Registrate ──
  const [registrate, setRegistrate] = useState<FatturaPassiva[]>([]);
  const [pagaId, setPagaId] = useState<string | null>(null);
  const [pagaData, setPagaData] = useState('');
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [dettaglioId, setDettaglioId] = useState<string | null>(passivaDeepLink);
  const loadRegistrate = useCallback(() => {
    void (async () => { setRegistrate(await listFatturePassive()); })();
  }, []);
  useEffect(() => { if (tab === 'registrate') loadRegistrate(); }, [tab, loadRegistrate]);

  async function segnaPagata(id: string) {
    if (!pagaData) return;
    await updateFatturaPassiva(id, { pagata: true, data_pagamento: pagaData });
    setPagaId(null); setPagaData(''); loadRegistrate();
  }
  async function elimina(id: string) {
    await deleteFatturaPassiva(id); setConfirmDel(null); loadRegistrate();
  }
  async function scarica(f: FatturaPassiva) {
    const blob = await getFilePassivaBlob(f.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = f.file_name ?? `fattura_passiva_${f.id}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  const tabs: [Tab, string][] = [
    ['da-registrare', t('fatturazione.passive.tab_da_registrare')],
    ['registra', t('fatturazione.passive.tab_registra')],
    ['registrate', t('fatturazione.passive.tab_registrate')],
  ];

  return (
    <div>
      <div className="flex gap-1 mb-5 border-b border-stone-mid">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            data-testid={`tab-passive-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === k ? 'border-sage text-sage-dark' : 'border-transparent text-ink-soft hover:text-ink-dark'
            }`}>
            {label}{k === 'da-registrare' && totSelezionati > 0 ? ` (${totSelezionati})` : ''}
          </button>
        ))}
      </div>

      {/* ── Da registrare ── */}
      {tab === 'da-registrare' && (
        <div>
          <div className="flex mb-4 gap-2">
            <button type="button" disabled={fornitoreItems.length === 0} onClick={() => setProspettoOpen(true)}
              data-testid="btn-prospetto-fornitore"
              className="ml-auto px-3 py-1.5 text-sm font-medium rounded border border-sage text-sage-dark hover:bg-sage/10 disabled:opacity-40 disabled:cursor-not-allowed">
              {t('fatturazione.prospetto.apri_btn')}
            </button>
            <button type="button" disabled={totSelezionati === 0} onClick={() => setTab('registra')}
              data-testid="btn-registra-selezione"
              className="px-3 py-1.5 text-sm font-medium rounded bg-sage text-white disabled:opacity-40 disabled:cursor-not-allowed">
              {t('fatturazione.passive.registra_btn', { n: totSelezionati })}
            </button>
          </div>
          {lockedCustomer && (
            <p className="text-xs text-ink-soft mb-2">
              {t('fatturazione.passive.locked_fornitore')}
            </p>
          )}
          <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-bg">
                <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  <th className="px-3 py-3"></th>
                  <th className="px-3 py-3">{t('fatturazione.passive.col_fornitore')}</th>
                  <th className="px-3 py-3">{t('fatturazione.passive.col_conferimento')}</th>
                  <th className="px-3 py-3">{t('fatturazione.passive.col_categoria')}</th>
                  <th className="px-3 py-3 text-right">{t('fatturazione.passive.col_peso')}</th>
                </tr>
              </thead>
              <tbody>
                {daReg.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.passive.vuoto_da_registrare')}</td></tr>
                ) : daReg.map((d) => {
                  const disabled = lockedCustomer != null && d.customer_id !== lockedCustomer;
                  return (
                    <tr key={d.conferimento_id} className="border-t border-stone-mid/60" data-testid={`da-registrare-${d.conferimento_id}`}>
                      <td className="px-3 py-2.5">
                        <input type="checkbox" className="accent-sage" disabled={disabled}
                          checked={selected.has(d.conferimento_id)} onChange={() => toggle(d)} />
                      </td>
                      <td className="px-3 py-2.5">{d.customer_ragione_sociale}</td>
                      <td className="px-3 py-2.5 font-medium">{d.numero ?? d.codice_ordine}
                        {d.numero_fir && <span className="block text-[11px] text-ink-faint font-normal">{t('fatturazione.passive.fir', { fir: d.numero_fir })}</span>}
                      </td>
                      <td className="px-3 py-2.5">{categoriaLabel(d.categoria, categorie)}</td>
                      <td className="px-3 py-2.5 text-right">{d.peso_netto_kg ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Uscite a terzi: sezione separata, stesse regole di selezione.
               Nessun filtro categoria: un'uscita non ne ha, e passarla dal
               filtro dei conferimenti la farebbe sparire al primo filtro. ── */}
          <h3 className="text-sm font-semibold text-ink-dark mt-6 mb-2" data-testid="titolo-uscite-terzi">
            {t('fatturazione.passive.uscite_titolo')}
          </h3>
          <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-bg">
                <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                  <th className="px-3 py-3"></th>
                  <th className="px-3 py-3">{t('fatturazione.passive.col_fornitore')}</th>
                  <th className="px-3 py-3">{t('fatturazione.passive.col_uscita')}</th>
                  <th className="px-3 py-3">{t('fatturazione.passive.col_rifiuto')}</th>
                  <th className="px-3 py-3 text-right">{t('fatturazione.passive.col_peso')}</th>
                </tr>
              </thead>
              <tbody>
                {uscite.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.passive.vuoto_uscite')}</td></tr>
                ) : uscite.map((u) => {
                  const disabled = lockedCustomer != null && u.customer_id !== lockedCustomer;
                  return (
                    <tr key={u.uscita_id} className="border-t border-stone-mid/60" data-testid={`uscita-da-registrare-${u.uscita_id}`}>
                      <td className="px-3 py-2.5">
                        <input type="checkbox" className="accent-sage" disabled={disabled}
                          checked={selUscite.has(u.uscita_id)} onChange={() => toggleUscita(u)} />
                      </td>
                      <td className="px-3 py-2.5">{u.customer_ragione_sociale}</td>
                      <td className="px-3 py-2.5 font-medium">{u.codice}
                        <span className="block text-[11px] text-ink-faint font-normal">{t('fatturazione.passive.fir', { fir: u.numero_fir })}</span>
                      </td>
                      <td className="px-3 py-2.5">{u.descrizione_rifiuto}
                        <span className="ml-2 text-xs text-ink-faint">{u.cer_code}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right">{fmtKg(u.peso_kg)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Registra fattura ── */}
      {tab === 'registra' && (
        <div className="max-w-xl">
          {totSelezionati === 0 ? (
            <p className="text-sm text-ink-soft">{t('fatturazione.passive.nessuna_selezione')}</p>
          ) : (
            <div className="bg-white border border-stone-mid rounded-xl p-5 space-y-4">
              <div>
                {selectedItems.length > 0 && (
                  <p className="text-xs font-medium text-ink-soft mb-1">{t('fatturazione.passive.conf_selezionati', { n: selectedItems.length })}</p>
                )}
                <p className="text-sm text-ink-dark">
                  {t('fatturazione.passive.fornitore_label')}{' '}
                  <strong>{selectedItems[0]?.customer_ragione_sociale ?? selectedUscite[0]?.customer_ragione_sociale}</strong>
                </p>
                <ul className="text-xs text-ink-soft mt-1 list-disc pl-4">
                  {selectedItems.map((d) => <li key={d.conferimento_id}>{d.numero ?? d.codice_ordine}</li>)}
                </ul>
                {selectedUscite.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-ink-soft mt-2 mb-1" data-testid="form-uscite-selezionate">
                      {t('fatturazione.passive.uscite_selezionate', { n: selectedUscite.length })}
                    </p>
                    <ul className="text-xs text-ink-soft list-disc pl-4">
                      {selectedUscite.map((u) => <li key={u.uscita_id}>{u.codice} · {u.cer_code}</li>)}
                    </ul>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-ink-soft mb-1">{t('fatturazione.passive.lbl_numero')}</label>
                  <input className={inputCls + ' w-full'} data-testid="passiva-numero" value={numeroForn} onChange={(e) => setNumeroForn(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-ink-soft mb-1">{t('fatturazione.passive.lbl_data')}</label>
                  <input type="date" className={inputCls + ' w-full'} data-testid="passiva-data" value={dataFatt} onChange={(e) => setDataFatt(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-ink-soft mb-1">{t('fatturazione.passive.lbl_importo')}</label>
                  <input type="number" min="0" step="0.01" className={inputCls + ' w-full'} data-testid="passiva-importo" value={importo} onChange={(e) => setImporto(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-ink-soft mb-1">{t('fatturazione.passive.lbl_file')}</label>
                  <label className="flex items-center gap-1.5 text-sm text-sage-dark cursor-pointer border border-stone-border rounded px-2 py-1.5 hover:bg-stone-bg">
                    <Upload size={14} /> {file ? file.name : t('fatturazione.passive.scegli_file')}
                    <input type="file" accept=".pdf,.xml,application/pdf,application/xml,text/xml" className="hidden"
                      data-testid="passiva-file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="accent-sage" checked={pagata} onChange={(e) => setPagata(e.target.checked)} />
                  {t('fatturazione.passive.gia_pagata')}
                </label>
                {pagata && (
                  <div>
                    <input type="date" className={inputCls} data-testid="passiva-data-pagamento" value={dataPag} onChange={(e) => setDataPag(e.target.value)} />
                  </div>
                )}
              </div>
              {formErr && <p className="text-xs text-red-700 flex items-start gap-1"><AlertCircle size={14} className="mt-px" />{formErr}</p>}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setTab('da-registrare')} className="px-3 py-2 text-sm border border-stone-border rounded-lg text-ink-mid hover:bg-stone-bg">{t('fatturazione.passive.indietro')}</button>
                <button type="button" onClick={submitRegistra} disabled={saving}
                  data-testid="passiva-submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark disabled:opacity-60">
                  {saving ? t('fatturazione.passive.registrazione_in_corso') : t('fatturazione.passive.registra_fattura')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Registrate ── */}
      {tab === 'registrate' && (
        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-bg">
              <tr className="text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                <th className="px-3 py-3">{t('fatturazione.passive.col_numero_forn')}</th>
                <th className="px-3 py-3">{t('fatturazione.passive.col_fornitore')}</th>
                <th className="px-3 py-3">{t('fatturazione.passive.col_data')}</th>
                <th className="px-3 py-3 text-right">{t('fatturazione.passive.col_importo')}</th>
                <th className="px-3 py-3">{t('fatturazione.passive.col_stato')}</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {registrate.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-ink-soft">{t('fatturazione.passive.vuoto_registrate')}</td></tr>
              ) : registrate.map((f) => (
                <tr key={f.id} className="border-t border-stone-mid/60 hover:bg-stone-bg/50" data-testid={`registrata-${f.id}`}>
                  <td className="px-3 py-2.5 font-medium">
                    <button type="button" onClick={() => setDettaglioId(f.id)} className="text-sage-dark hover:underline" data-testid={`btn-dettaglio-passiva-${f.id}`}>
                      {f.numero_fornitore}
                    </button>
                  </td>
                  <td className="px-3 py-2.5">{f.customer_ragione_sociale ?? '—'}</td>
                  <td className="px-3 py-2.5">{new Date(f.data).toLocaleDateString('it-IT')}</td>
                  <td className="px-3 py-2.5 text-right">{eur(f.importo)}</td>
                  <td className="px-3 py-2.5">
                    {f.pagata
                      ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">{f.data_pagamento ? t('fatturazione.passive.pagata_il', { data: new Date(f.data_pagamento).toLocaleDateString('it-IT') }) : t('fatturazione.passive.pagata')}</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">{t('fatturazione.passive.da_pagare')}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {f.has_file && (
                      <button type="button" onClick={() => scarica(f)} className="text-xs text-sage-dark hover:underline mr-3 inline-flex items-center gap-1">
                        <Download size={13} />{t('fatturazione.passive.file')}
                      </button>
                    )}
                    {!f.pagata && (
                      pagaId === f.id ? (
                        <span className="inline-flex items-center gap-1">
                          <input type="date" className={inputCls} value={pagaData} onChange={(e) => setPagaData(e.target.value)} />
                          <button type="button" onClick={() => segnaPagata(f.id)} className="text-xs text-sage-dark hover:underline">{t('fatturazione.passive.ok')}</button>
                          <button type="button" onClick={() => { setPagaId(null); setPagaData(''); }} className="text-xs text-ink-soft hover:underline">{t('fatturazione.passive.annulla')}</button>
                        </span>
                      ) : (
                        <button type="button" onClick={() => { setPagaId(f.id); setPagaData(''); }} className="text-xs text-sage-dark hover:underline mr-3">{t('fatturazione.passive.segna_pagata')}</button>
                      )
                    )}
                    {confirmDel === f.id ? (
                      <span className="text-xs">{t('fatturazione.passive.eliminare')}{' '}
                        <button type="button" onClick={() => elimina(f.id)} className="text-red-700 font-medium hover:underline">{t('fatturazione.passive.si')}</button>{' / '}
                        <button type="button" onClick={() => setConfirmDel(null)} className="text-ink-soft hover:underline">{t('fatturazione.passive.no')}</button>
                      </span>
                    ) : (
                      <button type="button" onClick={() => setConfirmDel(f.id)} className="text-xs text-red-700 hover:underline inline-flex items-center gap-1">
                        <Trash2 size={13} />{t('fatturazione.passive.elimina')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dettaglioId && <FatturaPassivaDettaglioModal fatturaId={dettaglioId} onClose={() => setDettaglioId(null)} />}
      {prospettoOpen && lockedCustomer && (
        <ProspettoPassivoModal
          customerId={lockedCustomer}
          fornitoreNome={fornitoreItems[0]?.customer_ragione_sociale ?? ''}
          items={fornitoreItems}
          onClose={() => setProspettoOpen(false)}
        />
      )}
    </div>
  );
}
